import { ApiRegister, ApiCustomer } from '@vend/types'
import { VdTracking, VdFeatureManager } from '@vend/vend.js'
import { isAmountEligibleForLSPayLink } from '@vend/business-components'
import ngRedux from 'ng-redux'
import { trackError } from '@vend/utilities'
import { RegisterManager } from '../../../managers/register-manager'
import { Sale } from '../../../models/sale'
import template from '../receipt-selection-popover/receipt-selection-popover.html'
import { ReceiptSettingsStore } from '../../../factories/database/receipt-settings-store'
import { $log, $q } from '../../../ng-compat/imports'
import { PosStateManager } from '../../../managers/pos-state-manager'
import { RetailerSettingsManager } from '../../../managers/retailer-settings-manager'
import { SaleStatus } from '../../../models/sale.constants'
import { PaymentManager } from '../../../managers/payment-manager'
import { SaleManager } from '../../../managers/sale-manager'
import {
  selectCurrentSale,
  selectCurrentSaleAsSale,
  updateReceiptValues,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { isQuote } from '../../../functions/sale'
interface ReceiptInformation {
  name: string
  id: string
}
export class ReceiptSelectionEmailToggleController {
  public email = ''
  public sendEmailCopy = 'Email to'
  public isCheckingPaymentLinkAvailability = false
  public shouldSendEmailReceipt = false
  public hasMultipleReceipts?: boolean
  public retailerIsTrialing?: boolean
  public receipt: ReceiptInformation
  private register: ApiRegister
  private sale?: Sale

  constructor(
    private readonly saleManager: SaleManager,
    private readonly vdPopover: any,
    private readonly receiptSettingsStore: ReceiptSettingsStore,
    private readonly registerManager: RegisterManager,
    private readonly posStateManager: PosStateManager,
    private readonly vdTracking: VdTracking,
    private readonly retailerSettingsManager: RetailerSettingsManager,
    private readonly paymentManager: PaymentManager,
    private vdFeatureManager: VdFeatureManager,
    private customerModals: $TSFixMe,
    private $ngRedux: ngRedux.INgRedux
  ) {
    'ngInject'

    this.register = this.registerManager.getActiveRegister()
    this.receipt = {
      name: '',
      id: '',
    }

    // Get the default receipt
    const receiptPromise = this.receiptSettingsStore
      .get(this.register.receipt_template_id)
      .then(receipt => {
        if (!receipt) {
          trackError('Failed to fetch receipt templates', {
            data: { templateId: this.register.receipt_template_id },
          })
          return
        }
        this.receipt = receipt
      })

    this.receiptSettingsStore
      .count()
      .then((numberOfReceipts: number) => {
        this.hasMultipleReceipts = numberOfReceipts > 1
      })
      .catch((error: $TSFixMe) => {
        $log.warn(
          'Failed to retrieve settings from receiptSettings store',
          error
        )
      })

    this.retailerIsTrialing =
      this.retailerSettingsManager.get().account_status === 'trialing'

    // Initiate the saleManager and get some initial values
    const salePromise = this.saleManager.init().then(() => {
      this.sale = selectCurrentSaleAsSale(this.$ngRedux.getState())
      if (this.sale === undefined) {
        $log.warn('Could not find sale to attach to receipt.')
        return
      }

      // Get the email from the customer attached to the sale
      if (this.sale.customer && this.sale.customer.email) {
        this.email = this.sale.customer.email
        if (isQuote(this.sale.status)) {
          this.sendEmailCopy = 'Email quote to'
        } else if (this.sale.isSavedService) {
          this.sendEmailCopy = 'Email an updated receipt to'
        } else {
          this.sendEmailCopy = 'Email a receipt to'
        }
        // We only need to check for the payment link availability if FF is enabled and
        // the sale is an on-account sale with an outstanding balance within the range
        if (
          this.vdFeatureManager.get('lspay_payment_links') &&
          this.sale.status === SaleStatus.ACCOUNT_SALE &&
          isAmountEligibleForLSPayLink(this.sale.getOutstandingBalance())
        ) {
          this.isCheckingPaymentLinkAvailability = true
          // Check for the payment link availability for the current on-account sale
          // and update the sendEmailCopy for an eligible sale
          const { id: activeOutletId } = this.registerManager.getActiveOutlet()
          this.paymentManager
            .retrievePaymentLinkAvailability(activeOutletId)
            .then((isEligible: boolean) => {
              if (isEligible) {
                this.sendEmailCopy = `Email ${
                  this.sale?.isSavedService ? 'an updated' : 'a'
                } receipt with pay link to`
              }
            })
            .catch((error: $TSFixMe) => {
              $log.warn('[Sale confirmation screen]', error)
            })
            .finally(() => {
              this.isCheckingPaymentLinkAvailability = false
            })
        }
      }

      // Get the initial toggle value from the register settings
      if (this.register && this.sale) {
        this.shouldSendEmailReceipt =
          !this.posStateManager.getTrainingModeEnabled() &&
          !this.retailerIsTrialing &&
          this.register.email_receipt
      }
    })

    // Once all settings have been considered,
    // update the sale with the correct values
    $q.all([receiptPromise, salePromise]).then(() => this.updateSaleValues())
  }

  /**
   * updateSaleValues updates the receipt values in the sale manager
   */
  public updateSaleValues() {
    if (!this.sale) {
      return
    }

    if (this.shouldSendEmailReceipt) {
      this.$ngRedux.dispatch(
        updateReceiptValues({
          receiptEmailAddress: this.email,
          receiptID: this.receipt.id,
        })
      )
    } else {
      this.$ngRedux.dispatch(
        updateReceiptValues({
          receiptEmailAddress: '',
          receiptID: isQuote(this.sale.status) ? this.receipt.id : '', // receipt template ID to be used for printing after quote creation
        })
      )
    }

    this.saleManager.saveSale()
  }

  /**
   * selectReceipt opens the receipt selection popover, and changes the name
   * @param event - clicking the current template name
   */
  public selectReceipt(event: { currentTarget: any }) {
    this.vdPopover
      .open({
        trigger: event.currentTarget,
        template,
        controller: 'ReceiptSelectionPopoverController',
        hasPopoverList: true,
        direction: 'center',
        openAsModalAtViewport: 'xsmall',
      })
      .then((modal: any) => {
        modal.closed.then((data?: { selectedReceipt: ReceiptInformation }) => {
          if (data) {
            const { selectedReceipt } = data
            if (selectedReceipt.id !== this.receipt.id) {
              this.receipt = selectedReceipt
              this.updateSaleValues()
            }
          }
        })
      })
  }

  public editCustomerInformation() {
    const currentSale = selectCurrentSale(this.$ngRedux.getState())
    this.vdTracking.trackUserEvent({
      eventName: 'Clicked edit email on completion screen',
      metadata: {
        id: currentSale.localId,
        customerId: currentSale.customer?.id,
      },
    })
    this.customerModals
      .updateCustomer(currentSale.customer)
      .then((updatedCustomer?: ApiCustomer) => {
        if (updatedCustomer?.email) {
          if (this.email !== updatedCustomer.email) {
            this.vdTracking.trackUserEvent({
              eventName: 'Updated email on completion screen',
              metadata: {
                id: currentSale.localId,
                customerId: currentSale.customer?.id,
              },
            })
          }
          this.email = updatedCustomer.email
          this.updateSaleValues()
        }
      })
  }
}
